import { useState, useEffect, useRef, useCallback } from "react";
import { Play, Pause, RotateCcw, Loader2, AlertCircle, Film, Clock, Check } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { M_URL } from "../../constants";


function getAdminToken() {
  try {
    return window.localStorage?.getItem("admin_token") || "";
  } catch (e) {
    return "";
  }
}

function authHeaders(extra = {}) {
  const token = getAdminToken();
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
}

// ---- helpers -------------------------------------------------

function useLocalTaskId() {
  const [taskId, setTaskId] = useState(null);
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    try {
      const stored = window.localStorage?.getItem("taskId");
      if (stored) setTaskId(stored);
    } catch (e) {
      // localStorage unavailable in this environment
    } finally {
      setChecked(true);
    }
  }, []);
  return { taskId, checked };
}

function StatusPill({ kind, children }) {
  const styles = {
    idle: { bg: "var(--surface-2)", fg: "var(--text-dim)", dot: "var(--text-dim)" },
    busy: { bg: "rgba(247,181,56,0.12)", fg: "var(--accent)", dot: "var(--accent)" },
    error: { bg: "rgba(229,93,75,0.12)", fg: "var(--danger)", dot: "var(--danger)" },
    ok: { bg: "rgba(90,200,140,0.12)", fg: "var(--success)", dot: "var(--success)" },
  };
  const s = styles[kind] || styles.idle;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: 999,
        background: s.bg,
        color: s.fg,
        fontSize: 12,
        fontFamily: "var(--font-mono)",
        letterSpacing: "0.02em",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: s.dot, flexShrink: 0 }} />
      {children}
    </span>
  );
}

// ---- single page card -----------------------------------------

function PageCard({ pageData, taskId, index, onRefreshPage }) {
  const { page, speach_text, video } = pageData;
  const [text, setText] = useState(speach_text || "");
  const [editing, setEditing] = useState(false);
  // status: idle | submitting | waiting | ready | error
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const taRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    setText(speach_text || "");
  }, [speach_text]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  const autosize = useCallback(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  }, []);

  useEffect(() => {
    autosize();
  }, [text, autosize]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setIsPlaying(true);
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const connectWebSocket = useCallback(() => {
    // Close any existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    const token = getAdminToken();
    const wsUrl = `wss://videogenerator.soof.uz/ws/notifications?token=${token}&task_id=${taskId}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      // Connected, now in "waiting" state
      setStatus("waiting");
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Look for a "ready" event — adapt field names to your backend's shape
        const eventType = data?.event || data?.type || data?.status || "";
        const eventPage = data?.page ?? data?.page_number ?? null;

        // Only react to events for this specific page (if page info is present)
        if (eventPage !== null && Number(eventPage) !== Number(page)) return;

        if (eventType === "ready" || eventType === "completed" || eventType === "done") {
          ws.close();
          wsRef.current = null;
          setStatus("ready");
          // Trigger parent refresh for this page
          if (onRefreshPage) onRefreshPage(page);
        } else if (eventType === "error" || eventType === "failed") {
          ws.close();
          wsRef.current = null;
          setStatus("error");
          setErrMsg(data?.message || data?.detail || "Generatsiya xatosi");
        }
      } catch (e) {
        // Non-JSON message, ignore
      }
    };

    ws.onerror = () => {
      setStatus("error");
      setErrMsg("WebSocket ulanishda xatolik yuz berdi");
      wsRef.current = null;
    };

    ws.onclose = (event) => {
      // If still in "waiting" and socket closes unexpectedly (not clean), show error
      if (event.code !== 1000) {
        setStatus((prev) => {
          if (prev === "waiting") {
            setErrMsg("Ulanish uzildi. Iltimos qayta urinib ko'ring.");
            return "error";
          }
          return prev;
        });
      }
      wsRef.current = null;
    };
  }, [taskId, page, onRefreshPage]);

  const handleRegenerate = async () => {
    setStatus("submitting");
    setErrMsg("");
    try {
      const res = await fetch(
        `${M_URL}/video-generate/regenerate-page/${encodeURIComponent(taskId)}`,
        {
          method: "POST",
          headers: authHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ page, speach_text: text }),
        }
      );
      if (!res.ok) {
        let detail = `Server javobi: ${res.status}`;
        try {
          const data = await res.json();
          if (data?.detail) {
            detail = Array.isArray(data.detail)
              ? data.detail.map((d) => d.msg).join("; ")
              : String(data.detail);
          }
        } catch (e) {
          /* ignore parse error */
        }
        throw new Error(detail);
      }
      // POST succeeded — now open WebSocket to wait for "ready"
      connectWebSocket();
    } catch (e) {
      setStatus("error");
      setErrMsg(e.message || "Noma'lum xatolik yuz berdi");
    }
  };

  const dirty = text !== (speach_text || "");
  const isWaiting = status === "submitting" || status === "waiting";

  return (
    <div
      style={{
        background: "var(--surface-1)",
        border: "1px solid var(--border)",
        borderRadius: 14,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      {/* header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          borderBottom: "1px solid var(--border)",
          background: "var(--surface-2)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--text-dim)",
              letterSpacing: "0.04em",
            }}
          >
            SAHIFA
          </span>
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 17,
              fontWeight: 700,
              color: "var(--text)",
            }}
          >
            {String(page).padStart(2, "0")}
          </span>
        </div>
        {status === "submitting" && (
          <StatusPill kind="busy">
            <Loader2 size={11} className="spin" /> Yuborilmoqda
          </StatusPill>
        )}
        {status === "waiting" && (
          <StatusPill kind="busy">
            <Loader2 size={11} className="spin" /> Generatsiya kutilmoqda
          </StatusPill>
        )}
        {status === "ready" && (
          <StatusPill kind="ok">
            <Check size={11} /> Tayyor — yangilandi
          </StatusPill>
        )}
        {status === "error" && <StatusPill kind="error">Xatolik</StatusPill>}
        {status === "idle" && <StatusPill kind="idle">Tayyor</StatusPill>}
      </div>

      {/* body: video + text side by side */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        {/* video */}
        <div
          style={{
            position: "relative",
            background: "#0a0b0d",
            width: "100%",
            height: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Waiting overlay */}
          {isWaiting && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(10,11,13,0.72)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                zIndex: 2,
                backdropFilter: "blur(2px)",
              }}
            >
              <Loader2
                size={32}
                className="spin"
                style={{ color: "var(--accent)", strokeWidth: 2 }}
              />
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  color: "rgba(255,255,255,0.65)",
                  letterSpacing: "0.04em",
                }}
              >
                {status === "submitting" ? "So'rov yuborilmoqda..." : "Video generatsiya qilinmoqda..."}
              </span>
            </div>
          )}

          {video ? (
            <video
              ref={videoRef}
              src={video}
              style={{ width: "80%", height: "100%", objectFit: "contain", display: "block" }}
              onClick={togglePlay}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              controls
              playsInline
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                color: "var(--text-dim)",
              }}
            >
              <Film size={28} strokeWidth={1.5} />
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>Video topilmadi</span>
            </div>
          )}
        </div>

        {/* speech text */}
        <div
          style={{
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            flex: 1,
            minWidth: 280,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--text-dim)",
                letterSpacing: "0.04em",
              }}
            >
              MATN
            </span>
            {dirty && (
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--accent)",
                }}
              >
                o'zgartirildi
              </span>
            )}
          </div>
          <textarea
            ref={taRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setEditing(true);
              if (status === "error") setStatus("idle");
            }}
            rows={4}
            placeholder="Matn kiritilmagan"
            disabled={isWaiting}
            style={{
              width: "100%",
              minHeight: 140,
              resize: "vertical",
              background: "var(--surface-2)",
              border: `1px solid ${dirty ? "var(--accent)" : "var(--border)"}`,
              borderRadius: 8,
              padding: "10px 12px",
              color: "var(--text)",
              fontFamily: "var(--font-body)",
              fontSize: 14,
              lineHeight: 1.6,
              outline: "none",
              boxSizing: "border-box",
              transition: "border-color 0.15s ease",
              opacity: isWaiting ? 0.5 : 1,
            }}
          />

          {status === "error" && (
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                padding: "8px 10px",
                background: "rgba(229,93,75,0.08)",
                border: "1px solid rgba(229,93,75,0.25)",
                borderRadius: 8,
                fontSize: 12.5,
                color: "var(--danger)",
                fontFamily: "var(--font-body)",
              }}
            >
              <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{errMsg}</span>
            </div>
          )}

          {status === "waiting" && (
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                padding: "8px 10px",
                background: "rgba(247,181,56,0.08)",
                border: "1px solid rgba(247,181,56,0.25)",
                borderRadius: 8,
                fontSize: 12.5,
                color: "var(--accent)",
                fontFamily: "var(--font-body)",
              }}
            >
              <Loader2 size={14} className="spin" style={{ flexShrink: 0, marginTop: 1 }} />
              <span>Video generatsiya qilinmoqda.</span>
            </div>
          )}

          {status === "ready" && (
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                padding: "8px 10px",
                background: "rgba(90,200,140,0.08)",
                border: "1px solid rgba(90,200,140,0.25)",
                borderRadius: 8,
                fontSize: 12.5,
                color: "var(--success)",
                fontFamily: "var(--font-body)",
              }}
            >
              <Check size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>Video muvaffaqiyatli yangilandi.</span>
            </div>
          )}

          <div style={{ marginTop: "auto", paddingTop: 4, maxWidth: 260 }}>
            <button
              onClick={handleRegenerate}
              disabled={isWaiting || !text.trim()}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "10px 14px",
                borderRadius: 8,
                border: "1px solid var(--accent)",
                background: isWaiting ? "var(--surface-2)" : "var(--accent)",
                color: isWaiting ? "var(--accent)" : "#1a1206",
                fontFamily: "var(--font-mono)",
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.02em",
                cursor: isWaiting || !text.trim() ? "not-allowed" : "pointer",
                opacity: !text.trim() ? 0.5 : 1,
                transition: "all 0.15s ease",
              }}
            >
              {isWaiting ? (
                <>
                  <Loader2 size={14} className="spin" />
                  {status === "submitting" ? "YUBORILMOQDA" : "KUTILMOQDA..."}
                </>
              ) : (
                <>
                  <RotateCcw size={14} /> REGENERATE
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- main app ---------------------------------------------------

export default function VideoPagesReview() {
  const { taskId, checked } = useLocalTaskId();
  const [pages, setPages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resolvedTaskId, setResolvedTaskId] = useState("");
  const navigate = useNavigate();

  const fetchPages = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setError("");
    setPages(null);
    try {
      const res = await fetch(`${M_URL}/video-generate/pages/${encodeURIComponent(id)}`, {
        headers: authHeaders(),
      });
      if (res.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/login");
        return;
      }
      if (!res.ok) {
        let detail = `Server javobi: ${res.status}`;
        try {
          const data = await res.json();
          if (data?.detail) {
            detail = Array.isArray(data.detail)
              ? data.detail.map((d) => d.msg).join("; ")
              : String(data.detail);
          }
        } catch (e) {
          /* ignore */
        }
        throw new Error(detail);
      }
      const data = await res.json();
      setResolvedTaskId(data.task_id || id);
      setPages(Array.isArray(data.pages) ? data.pages : []);
    } catch (e) {
      setError(e.message || "Ma'lumotlarni olishda xatolik yuz berdi");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Refresh a single page after WebSocket "ready" event
  const handleRefreshPage = useCallback(async (pageNumber) => {
    const id = resolvedTaskId || taskId;
    if (!id) return;
    try {
      const res = await fetch(`${M_URL}/video-generate/pages/${encodeURIComponent(id)}`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      const updatedPages = Array.isArray(data.pages) ? data.pages : [];
      setPages((prev) => {
        if (!prev) return updatedPages;
        // Merge: replace only the refreshed page
        return prev.map((p) => {
          const updated = updatedPages.find((u) => u.page === p.page);
          return updated || p;
        });
      });
    } catch (e) {
      // Silent fail — page will just not update automatically
    }
  }, [resolvedTaskId, taskId]);

  const [loadingMerge, setLoadingMerge] = useState(false);
  const mergeVideos = async () => {
    setLoadingMerge(true);
    try {
      const response = await axios.post(
        `${M_URL}/video-generate/merge/${taskId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log(response.data);

      localStorage.setItem("videoUrl", response.data.video_url);
      localStorage.setItem("taskId", response.data.task_id);
      navigate("/preview");
      setLoadingMerge(false);

      return response.data;
    } catch (error) {
      console.error("Merge xatolik:", error.response?.data || error.message);
      setLoadingMerge(false);
    }
  };

  useEffect(() => {
    if (taskId) fetchPages(taskId);
  }, [taskId, fetchPages]);

  return (
    <div
      style={{
        "--surface-0": "#f7f6f3",
        "--surface-1": "#ffffff",
        "--surface-2": "#f0eee9",
        "--border": "#dfdcd4",
        "--text": "#1c1b18",
        "--text-dim": "#75726a",
        "--accent": "#c8780f",
        "--danger": "#c8402f",
        "--success": "#2f8a5c",
        "--font-display": "'Space Grotesk', 'Segoe UI', sans-serif",
        "--font-body": "'Inter', 'Segoe UI', sans-serif",
        "--font-mono": "'JetBrains Mono', 'Courier New', monospace",
        background: "var(--surface-0)",
        color: "var(--text)",
        minHeight: "100%",
        padding: "28px 20px 60px",
        boxSizing: "border-box",
        fontFamily: "var(--font-body)",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500;700&display=swap');
        .spin { animation: spin 0.9s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        ::placeholder { color: var(--text-dim); opacity: 0.7; }
        textarea:focus { border-color: var(--accent) !important; }
        video::-webkit-media-controls-panel { background-color: rgba(0,0,0,0.5); }
      `}</style>

      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        {/* header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <h1
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 28,
                fontWeight: 700,
                margin: 0,
                letterSpacing: "-0.01em",
              }}
            >
              Sahifalar ko'rinishi
            </h1>
            {resolvedTaskId && (
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  color: "var(--text-dim)",
                  padding: "3px 8px",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                }}
              >
                {resolvedTaskId}
              </span>
            )}
          </div>
          <p style={{ color: "var(--text-dim)", fontSize: 14, marginTop: 6, maxWidth: 640 }}>
            Har bir video va uning matnini ko'ring, matnni tahrirlang va kerak bo'lsa shu sahifani qayta generatsiya qiling.
          </p>
        </div>

        {/* no task id state */}
        {checked && !taskId && (
          <div
            style={{
              background: "var(--surface-1)",
              border: "1px solid var(--border)",
              borderRadius: 14,
              padding: 28,
              maxWidth: 460,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <AlertCircle size={18} color="var(--accent)" />
              <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16 }}>
                Task ID topilmadi
              </span>
            </div>
            <p style={{ color: "var(--text-dim)", fontSize: 13.5, lineHeight: 1.6 }}>
              localStorage'da <code style={{ fontFamily: "var(--font-mono)" }}>taskId</code> kaliti
              topilmadi. Avval video generatsiya jarayonini boshlang, so'ng bu sahifaga qayting.
            </p>
          </div>
        )}

        {/* loading */}
        {loading && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--text-dim)", padding: "40px 0" }}>
            <Loader2 size={18} className="spin" />
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>Sahifalar yuklanmoqda...</span>
          </div>
        )}

        {/* error */}
        {!loading && error && (
          <div
            style={{
              background: "rgba(229,93,75,0.08)",
              border: "1px solid rgba(229,93,75,0.3)",
              borderRadius: 12,
              padding: 20,
              maxWidth: 560,
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
            }}
          >
            <AlertCircle size={18} color="var(--danger)" style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                Yuklab bo'lmadi
              </div>
              <div style={{ color: "var(--text-dim)", fontSize: 13.5, lineHeight: 1.5, marginBottom: 12 }}>
                {error}
              </div>
              <button
                onClick={() => fetchPages(taskId)}
                style={{
                  background: "var(--surface-2)",
                  border: "1px solid var(--border)",
                  color: "var(--text)",
                  borderRadius: 8,
                  padding: "8px 14px",
                  fontFamily: "var(--font-mono)",
                  fontSize: 12.5,
                  cursor: "pointer",
                }}
              >
                Qayta urinish
              </button>
            </div>
          </div>
        )}

        {/* empty */}
        {!loading && !error && pages && pages.length === 0 && (
          <div style={{ color: "var(--text-dim)", fontFamily: "var(--font-mono)", fontSize: 13.5 }}>
            Bu task uchun sahifalar topilmadi.
          </div>
        )}

        {/* list: one page per row */}
        {!loading && !error && pages && pages.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            {pages.map((p, i) => (
              <PageCard
                key={p.page ?? i}
                pageData={p}
                taskId={resolvedTaskId || taskId}
                index={i}
                onRefreshPage={handleRefreshPage}
              />
            ))}
          </div>
        )}
      </div>
      <button
        onClick={mergeVideos}
        className="confirmation-btn"
        style={{ margin: "auto", marginTop: 50 }}
        disabled={loadingMerge}
      >
        {loadingMerge ? "Merging pages..." : "Merge pages"}
      </button>
    </div>
  );
}